import { createStore } from '@xstate/store';
import type { ContextMenuItem } from '../../components/ContextMenu';

export interface ContextMenuState {
  x: number;
  y: number;
  items: ContextMenuItem[];
}

export type CanvasPattern = 'none' | 'dots' | 'grid' | 'crosses' | 'graph-paper';

const SIDEBAR_STORAGE_KEY = 'roadmap_2026_canvas_sidebar_width';
const SIDEBAR_COLLAPSED_KEY = 'roadmap_2026_canvas_sidebar_collapsed';
const CANVAS_PATTERN_KEY = 'roadmap_2026_canvas_canvas_pattern';
const SPRITE_CLICKTHROUGH_KEY = 'roadmap_2026_canvas_sprite_clickthrough';
const VALID_PATTERNS: CanvasPattern[] = ['none', 'dots', 'grid', 'crosses', 'graph-paper'];
const SIDEBAR_MIN = 180;
const SIDEBAR_MAX = 400;
const SIDEBAR_DEFAULT = 240;
const SIDEBAR_COLLAPSED_WIDTH = 24;

const ADR_DETAIL_STORAGE_KEY = 'roadmap_2026_canvas_adr_detail_width';
const ADR_DETAIL_COLLAPSED_KEY = 'roadmap_2026_canvas_adr_detail_collapsed';
const ADR_DETAIL_MIN = 280;
const ADR_DETAIL_MAX = 900;
const ADR_DETAIL_DEFAULT = 380;
const ADR_DETAIL_COLLAPSED_WIDTH = 0;

const RESOURCE_DETAIL_STORAGE_KEY = 'roadmap_2026_canvas_resource_detail_width';
const RESOURCE_DETAIL_COLLAPSED_KEY = 'roadmap_2026_canvas_resource_detail_collapsed';
const RESOURCE_DETAIL_MIN = 280;
const RESOURCE_DETAIL_MAX = 900;
const RESOURCE_DETAIL_DEFAULT = 380;
const RESOURCE_DETAIL_COLLAPSED_WIDTH = 0;

function loadInitialSidebarWidth(): number {
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored) {
      const val = Number(stored);
      if (val >= SIDEBAR_MIN && val <= SIDEBAR_MAX) return val;
    }
  } catch {}
  return SIDEBAR_DEFAULT;
}

function loadInitialSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {}
  return false;
}

function loadInitialAdrDetailWidth(): number {
  try {
    const stored = localStorage.getItem(ADR_DETAIL_STORAGE_KEY);
    if (stored) {
      const val = Number(stored);
      if (val >= ADR_DETAIL_MIN && val <= ADR_DETAIL_MAX) return val;
    }
  } catch {}
  return ADR_DETAIL_DEFAULT;
}

function loadInitialAdrDetailCollapsed(): boolean {
  try {
    return localStorage.getItem(ADR_DETAIL_COLLAPSED_KEY) === 'true';
  } catch {}
  return false;
}

function loadInitialResourceDetailWidth(): number {
  try {
    const stored = localStorage.getItem(RESOURCE_DETAIL_STORAGE_KEY);
    if (stored) {
      const val = Number(stored);
      if (val >= RESOURCE_DETAIL_MIN && val <= RESOURCE_DETAIL_MAX) return val;
    }
  } catch {}
  return RESOURCE_DETAIL_DEFAULT;
}

function loadInitialResourceDetailCollapsed(): boolean {
  try {
    return localStorage.getItem(RESOURCE_DETAIL_COLLAPSED_KEY) === 'true';
  } catch {}
  return false;
}

function loadInitialSpriteClickThrough(): boolean {
  try {
    return localStorage.getItem(SPRITE_CLICKTHROUGH_KEY) === 'true';
  } catch {}
  return false;
}

function loadInitialCanvasPattern(): CanvasPattern {
  try {
    const stored = localStorage.getItem(CANVAS_PATTERN_KEY);
    if (stored && VALID_PATTERNS.includes(stored as CanvasPattern)) return stored as CanvasPattern;
  } catch {}
  return 'dots';
}

export interface AddPickerState {
  layerId?: string;  // target layer; undefined = active layer
  screenPos?: { x: number; y: number };  // right-click position for entity placement
}

export interface UiStoreContext {
  addPickerState: AddPickerState | null;
  contextMenu: ContextMenuState | null;
  groupPickerState: { targetNodeIds: string[] } | null;
  sidebarWidth: number;
  isSidebarCollapsed: boolean;
  isSidebarResizing: boolean;
  layerPanelExpandedGroups: Set<string>;
  layerPanelCollapsedLayers: Set<string>;
  depExplorerActive: boolean;
  depExplorerFocusId: string | null;
  canvasPattern: CanvasPattern;
  spriteClickThrough: boolean;
  adrDetailWidth: number;
  isAdrDetailCollapsed: boolean;
  isAdrDetailResizing: boolean;
  adrDetailAdrId: string | null;
  resourceDetailWidth: number;
  isResourceDetailCollapsed: boolean;
  isResourceDetailResizing: boolean;
  resourceDetailResourceId: string | null;
}

export function createUiStore() {
  const collapsed = loadInitialSidebarCollapsed();
  const expandedWidth = loadInitialSidebarWidth();
  const adrDetailCollapsed = loadInitialAdrDetailCollapsed();
  const adrDetailExpandedWidth = loadInitialAdrDetailWidth();
  const resourceDetailCollapsed = loadInitialResourceDetailCollapsed();
  const resourceDetailExpandedWidth = loadInitialResourceDetailWidth();

  return createStore({
    context: {
      addPickerState: null,
      contextMenu: null,
      groupPickerState: null,
      sidebarWidth: collapsed ? SIDEBAR_COLLAPSED_WIDTH : expandedWidth,
      isSidebarCollapsed: collapsed,
      isSidebarResizing: false,
      layerPanelExpandedGroups: new Set<string>(),
      layerPanelCollapsedLayers: new Set<string>(),
      depExplorerActive: false,
      depExplorerFocusId: null,
      canvasPattern: loadInitialCanvasPattern(),
      spriteClickThrough: loadInitialSpriteClickThrough(),
      adrDetailWidth: adrDetailCollapsed ? ADR_DETAIL_COLLAPSED_WIDTH : adrDetailExpandedWidth,
      isAdrDetailCollapsed: adrDetailCollapsed,
      isAdrDetailResizing: false,
      adrDetailAdrId: null,
      resourceDetailWidth: resourceDetailCollapsed ? RESOURCE_DETAIL_COLLAPSED_WIDTH : resourceDetailExpandedWidth,
      isResourceDetailCollapsed: resourceDetailCollapsed,
      isResourceDetailResizing: false,
      resourceDetailResourceId: null,
    } as UiStoreContext,

    on: {
      'addPicker.set': (ctx, event: { state: AddPickerState | null }) => ({
        ...ctx,
        addPickerState: event.state,
      }),
      'contextMenu.set': (ctx, event: { menu: ContextMenuState | null }) => ({
        ...ctx,
        contextMenu: event.menu,
      }),
      'groupPicker.set': (ctx, event: { state: { targetNodeIds: string[] } | null }) => ({
        ...ctx,
        groupPickerState: event.state,
      }),
      'sidebar.update': (ctx, event: Partial<Pick<UiStoreContext, 'sidebarWidth' | 'isSidebarCollapsed' | 'isSidebarResizing'>>) => ({
        ...ctx,
        ...event,
      }),
      'layerPanel.toggleGroup': (ctx, event: { groupId: string }) => {
        const next = new Set(ctx.layerPanelExpandedGroups);
        if (next.has(event.groupId)) next.delete(event.groupId);
        else next.add(event.groupId);
        return { ...ctx, layerPanelExpandedGroups: next };
      },
      'layerPanel.toggleLayerCollapsed': (ctx, event: { layerId: string }) => {
        const next = new Set(ctx.layerPanelCollapsedLayers);
        if (next.has(event.layerId)) next.delete(event.layerId);
        else next.add(event.layerId);
        return { ...ctx, layerPanelCollapsedLayers: next };
      },
      'depExplorer.toggle': (ctx) => ({
        ...ctx,
        depExplorerActive: !ctx.depExplorerActive,
        depExplorerFocusId: ctx.depExplorerActive ? null : ctx.depExplorerFocusId,
      }),
      'depExplorer.setFocus': (ctx, event: { entityId: string | null }) => ({
        ...ctx,
        depExplorerFocusId: event.entityId,
      }),
      'depExplorer.activateAndFocus': (ctx, event: { entityId: string }) => ({
        ...ctx,
        depExplorerActive: true,
        depExplorerFocusId: event.entityId,
      }),
      'depExplorer.off': (ctx) => ({
        ...ctx,
        depExplorerActive: false,
        depExplorerFocusId: null,
      }),
      'canvasPattern.set': (ctx, event: { pattern: CanvasPattern }) => {
        try { localStorage.setItem(CANVAS_PATTERN_KEY, event.pattern); } catch {}
        return { ...ctx, canvasPattern: event.pattern };
      },
      'spriteClickThrough.toggle': (ctx) => {
        const next = !ctx.spriteClickThrough;
        try { localStorage.setItem(SPRITE_CLICKTHROUGH_KEY, String(next)); } catch {}
        return { ...ctx, spriteClickThrough: next };
      },
      'adrDetail.update': (ctx, event: Partial<Pick<UiStoreContext, 'adrDetailWidth' | 'isAdrDetailCollapsed' | 'isAdrDetailResizing'>>) => ({
        ...ctx,
        ...event,
      }),
      'adrDetail.open': (ctx, event: { adrId: string }) => ({
        ...ctx,
        adrDetailAdrId: event.adrId,
        isAdrDetailCollapsed: false,
        adrDetailWidth: ctx.isAdrDetailCollapsed ? loadInitialAdrDetailWidth() : ctx.adrDetailWidth,
        // Mutual exclusivity: close resource detail
        resourceDetailResourceId: null,
      }),
      'adrDetail.close': (ctx) => ({
        ...ctx,
        adrDetailAdrId: null,
      }),
      'resourceDetail.update': (ctx, event: Partial<Pick<UiStoreContext, 'resourceDetailWidth' | 'isResourceDetailCollapsed' | 'isResourceDetailResizing'>>) => ({
        ...ctx,
        ...event,
      }),
      'resourceDetail.open': (ctx, event: { resourceId: string }) => ({
        ...ctx,
        resourceDetailResourceId: event.resourceId,
        isResourceDetailCollapsed: false,
        resourceDetailWidth: ctx.isResourceDetailCollapsed ? loadInitialResourceDetailWidth() : ctx.resourceDetailWidth,
        // Mutual exclusivity: close ADR detail
        adrDetailAdrId: null,
      }),
      'resourceDetail.close': (ctx) => ({
        ...ctx,
        resourceDetailResourceId: null,
      }),
    },
  });
}

export type CanvasUiStore = ReturnType<typeof createUiStore>;
